import React, { useEffect, useCallback, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';
import { useDashboardStore } from '../../stores/dashboardStore';
import { apiGet } from '../../lib/api-client';

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function TrendChart({
  data,
  dataKey,
  label,
  color,
  maxY,
}: {
  data: number[];
  dataKey: string;
  label: string;
  color: string;
  maxY?: number;
}) {
  const chartData = data.map((v, i) => ({ session: i + 1, [dataKey]: Math.round(v * 10) / 10 }));
  if (chartData.length === 0) return <div className="chart-empty">No data yet</div>;

  return (
    <div className="chart-container">
      <h4 className="chart-title">{label}</h4>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="session" tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <YAxis domain={[0, maxY ?? 'auto']} tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <Tooltip
            contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
            labelStyle={{ color: '#e5e7eb' }}
            itemStyle={{ color }}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScoreDistChart({ data }: { data: { bucket: string; count: number }[] }) {
  if (!data || data.length === 0) return <div className="chart-empty">No data yet</div>;
  return (
    <div className="chart-container">
      <h4 className="chart-title">Score Distribution</h4>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <Tooltip
            contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
          />
          <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function IsolationChart({ data }: { data: Record<string, number> }) {
  const streets = ['preflop', 'flop', 'turn', 'river', 'none'];
  const chartData = streets.map(s => ({ street: s, count: data[s] ?? 0 }));
  const colors: Record<string, string> = {
    preflop: '#6366f1', flop: '#10b981', turn: '#f59e0b', river: '#ef4444', none: '#6b7280',
  };

  return (
    <div className="chart-container">
      <h4 className="chart-title">Street of Isolation</h4>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="street" tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
          <Tooltip
            contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
          />
          <Bar dataKey="count" radius={[3, 3, 0, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.street} fill={colors[entry.street]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DashboardScreen() {
  const { data, filters, loading, fetchData, setFilters } = useDashboardStore();
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const handleExport = useCallback(async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      const query: Record<string, string> = { format };
      if (filters) query.filters = JSON.stringify(filters);
      const data = await apiGet<unknown>('/api/sessions/export', query);
      // Trigger browser download
      const blob = new Blob(
        [format === 'json' ? JSON.stringify(data, null, 2) : String(data)],
        { type: format === 'json' ? 'application/json' : 'text/csv' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `range-reader-sessions-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setExporting(false);
    }
  }, [filters]);

  return (
    <div className="dashboard-screen">
      <div className="dashboard-header">
        <h2>Session Statistics</h2>

        {/* Filter bar */}
        <div className="dashboard-filters">
          <select
            className="filter-select-sm"
            value={filters.stakeLevel}
            onChange={e => setFilters({ stakeLevel: e.target.value as any })}
          >
            <option value="any">Any Stakes</option>
            <option value="micro">Micro</option>
            <option value="low">Low</option>
            <option value="mid">Mid</option>
            <option value="high">High</option>
          </select>

          <select
            className="filter-select-sm"
            value={filters.tableType}
            onChange={e => setFilters({ tableType: e.target.value as any })}
          >
            <option value="any">Any Table</option>
            <option value="6">6-max</option>
            <option value="9">9-max</option>
          </select>

          <input
            type="date"
            className="filter-input-sm"
            value={filters.dateFrom ? new Date(filters.dateFrom).toISOString().slice(0, 10) : ''}
            onChange={e => setFilters({ dateFrom: e.target.value ? new Date(e.target.value).getTime() : null })}
          />
          <span className="filter-sep">to</span>
          <input
            type="date"
            className="filter-input-sm"
            value={filters.dateTo ? new Date(filters.dateTo).toISOString().slice(0, 10) : ''}
            onChange={e => setFilters({ dateTo: e.target.value ? new Date(e.target.value).getTime() : null })}
          />
        </div>
      </div>

      {loading && (
        <div className="dashboard-loading">
          <div className="loading-spinner" />
          <p>Loading statistics...</p>
        </div>
      )}

      {data && !loading && data.totalSessions > 0 && (
        <>
          {/* Summary stat cards */}
          <div className="stat-cards-row">
            <StatCard
              label="Total Sessions"
              value={data.totalSessions}
              sub={`${data.completedSessions} completed · ${data.skippedSessions} skipped`}
            />
            <StatCard label="Completion Rate" value={`${(data.completionRate * 100).toFixed(0)}%`} />
            <StatCard label="Hit Rate" value={`${(data.hitRate * 100).toFixed(0)}%`} sub="Hand in final range" />
            <StatCard label="Near-Miss Rate" value={`${(data.nearMissRate * 100).toFixed(0)}%`} sub="Of misses" />
          </div>

          {/* Export buttons */}
          <div className="dashboard-export-row">
            <button className="btn-export" onClick={() => handleExport('csv')} disabled={exporting}>
              Export CSV
            </button>
            <button className="btn-export" onClick={() => handleExport('json')} disabled={exporting}>
              Export JSON
            </button>
          </div>

          {/* Trend charts */}
          <div className="charts-grid">
            <TrendChart
              data={data.avgAccuracyScore}
              dataKey="score"
              label="Accuracy Score (last 30 sessions)"
              color="#3b82f6"
              maxY={100}
            />
            <TrendChart
              data={data.avgStreetBonus}
              dataKey="bonus"
              label="Street Bonus Trend"
              color="#f59e0b"
              maxY={25}
            />
            <TrendChart
              data={data.avgNearMissCredit}
              dataKey="credit"
              label="Near-Miss Credit Trend"
              color="#8b5cf6"
              maxY={10}
            />
            <TrendChart
              data={data.avgFinalRangeWidth}
              dataKey="width"
              label="Final Range Width Trend"
              color="#10b981"
            />
            <ScoreDistChart data={data.scoreDistribution} />
            <IsolationChart data={data.streetIsolationBreakdown} />
          </div>
        </>
      )}

      {data && data.totalSessions === 0 && (
        <div className="dashboard-empty">
          <p>No sessions yet. Start training to see your statistics here!</p>
        </div>
      )}

      {!data && !loading && (
        <div className="dashboard-empty">
          <p>Failed to load statistics. Try refreshing the page.</p>
        </div>
      )}
    </div>
  );
}
